import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../core/services/api.service';
import { API_ROUTES } from '../../../core/constants/api-routes';
import { LayoutService } from '../../../core/services/layout.service';
import { ToastService } from '../../../core/services/toast.service';
import { StoryCategory } from '../../../core/models/story.models';

// -- Public interfaces --------------------------------------------------------

export interface Question {
  id: string;
  text: string;
  selected: boolean;
  answered: boolean;
  source?: string;
  sourceType?: number;
  isCustom?: boolean;
}

export interface QuestionTimeframe {
  questionId: string;
  startSecond: number;
  endSecond: number;
}

// -- Component ----------------------------------------------------------------

@Component({
  selector: 'app-story-record',
  templateUrl: './story-record.html',
  styleUrl: './story-record.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '(document:click)': 'closeAllMenus()' },
  imports: [CommonModule, FormsModule],
})
export class StoryRecord implements OnInit, OnDestroy {
  private readonly router        = inject(Router);
  private readonly layoutService = inject(LayoutService);
  private readonly api           = inject(ApiService);
  private readonly toast         = inject(ToastService);
  private readonly cdr           = inject(ChangeDetectorRef);
  private readonly zone          = inject(NgZone);
  private readonly platformId    = inject(PLATFORM_ID);

  // -- Step navigation -------------------------------------------------------

  readonly currentStep = signal<number>(1);
  readonly totalSteps  = 4;

  goToStep(step: number): void {
    if (step >= 1 && step <= this.totalSteps) this.currentStep.set(step);
  }

  nextStep(): void {
    const next = this.currentStep() + 1;
    this.goToStep(next);
    if (next === 3) {
      const catId = this.selectedCategory();
      if (catId) this.loadPrompts(catId);
    }
    if (next === 4) {
      this.currentQuestionIndex.set(0);
      this.recordingState.set('idle');
      this.recordedUrl.set(null);
      this.uploadedUrl.set(null);
      this.recordingTimer.set(0);
      this.questionTimeframes.set({});
      this.currentQuestionStartTime = 0;
    }
  }

  prevStep(): void { this.goToStep(this.currentStep() - 1); }

  cancelRecord(): void { this.router.navigate(['/app/stories']); }

  // -- Step 1: Categories ----------------------------------------------------

  readonly loadingCategories = signal(false);
  readonly apiCategories     = signal<StoryCategory[]>([]);
  readonly selectedCategory  = signal<number | null>(null);

  selectCategory(id: number): void { this.selectedCategory.set(id); }

  private readonly categoryIconMap: Record<number, string> = {
    1: 'images/icons/memory-share-icons/fm-ms-icon1.svg',
    2: 'images/icons/memory-share-icons/fm-ms-icon2.svg',
    3: 'images/icons/memory-share-icons/fm-ms-icon4.svg',
    4: 'images/icons/memory-share-icons/fm-ms-icon3.svg',
    5: 'images/icons/memory-share-icons/fm-ms-icon5.svg',
    6: 'images/icons/memory-share-icons/fm-ms-icon6.svg',
    7: 'images/icons/memory-share-icons/fm-ms-icon7.svg',
    8: 'images/icons/memory-share-icons/fm-ms-icon8.svg',
  };

  getCategoryIcon(cat: StoryCategory): string {
    return cat.iconUrl?.trim() || this.categoryIconMap[cat.categoryId] || 'images/icons/memory-share-icons/fm-ms-icon1.svg';
  }

  async loadCategories(): Promise<void> {
    this.loadingCategories.set(true);
    try {
      const res = await this.api.request<StoryCategory[]>(
        'GET', API_ROUTES.GET_ACTIVE_CATEGORIES, undefined,
        { showLoader: false, showToaster: false },
      );
      if (res.status && res.data) {
        this.apiCategories.set(res.data);
        if (res.data.length) this.selectedCategory.set(res.data[0].categoryId);
      }
    } finally {
      this.loadingCategories.set(false);
    }
  }

  // -- Step 2: Media type ----------------------------------------------------

  readonly mediaTypes = [
    { id: 'video',       label: 'Video Recording', description: 'Record or upload video to share your story',  icon: 'images/icons/memory-share-icons/fm-ts-icon1.svg' },
    { id: 'audio',       label: 'Audio Recording', description: 'Record audio to tell your story',             icon: 'images/icons/memory-share-icons/fm-ts-icon2.svg' },
    { id: 'image-audio', label: 'Image + Audio',   description: 'Upload photos and describe them with audio',  icon: 'images/icons/memory-share-icons/fm-ts-icon3.svg' },
  ];

  readonly selectedMedia = signal<string>('video');

  selectMedia(id: string): void { this.selectedMedia.set(id); }

  // -- Step 3: Questions -----------------------------------------------------

  readonly loadingPrompts     = signal(false);
  readonly questions          = signal<Question[]>([]);
  readonly selectedQuestions  = computed(() => this.questions().filter(q => q.selected));
  readonly openMenuId         = signal<string | null>(null);
  readonly showCustomQuestion = signal(false);
  readonly customQuestionText = signal('');
  readonly customQuestionTouched = signal(false);
  readonly editingQuestionId  = signal<string | null>(null);
  readonly editingQuestionText = signal('');
  readonly deleteConfirmId     = signal<string | null>(null);
  readonly isSavingCustomQuestion = signal(false);
  readonly isSavingEditQuestion   = signal(false);
  readonly showQuestionRequiredAlert = signal(false);

  continueFromStep3(): void {
    if (this.selectedQuestions().length === 0) {
      this.showQuestionRequiredAlert.set(true);
      return;
    }
    this.showQuestionRequiredAlert.set(false);
    this.nextStep();
  }

  async loadPrompts(categoryId: number): Promise<void> {
    this.loadingPrompts.set(true);
    this.questions.set([]);
    try {
      const res = await this.api.request<{ questionId: string; questionText: string; source: string; sourceType: number }[]>(
        'GET', `${API_ROUTES.GET_PROMPTS_FOR_CATEGORY}/${categoryId}`, undefined,
        { showLoader: false, showToaster: false },
      );
      const items = Array.isArray(res.data) ? res.data : res.data ? [res.data] : [];
      if (res.status && items.length) {
        this.questions.set(
          items.map(p => ({
            id: p.questionId,
            text: p.questionText,
            source: p.source,
            sourceType: p.sourceType,
            selected: false,
            answered: false,
            isCustom: p.sourceType === 2,
          })),
        );
      }
    } finally {
      this.loadingPrompts.set(false);
    }
  }

  toggleQuestion(id: string): void {
    this.questions.update(list => list.map(q => q.id === id ? { ...q, selected: !q.selected } : q));
    if (this.selectedQuestions().length > 0) this.showQuestionRequiredAlert.set(false);
  }

  deleteQuestion(id: string): void {
    this.openMenuId.set(null);
    this.deleteConfirmId.set(id);
  }

  cancelDelete(): void {
    this.deleteConfirmId.set(null);
  }

  confirmDelete(): void {
    const id = this.deleteConfirmId();
    if (!id) return;
    this.deleteConfirmId.set(null);
    const q = this.questions().find(q => q.id === id);
    if (q?.isCustom) {
      this.deleteCustomQuestionApi(id);
    } else {
      this.questions.update(list => list.filter(q => q.id !== id));
    }
  }

  selectAllQuestions(): void { this.questions.update(list => list.map(q => ({ ...q, selected: true  }))); }
  clearAllQuestions(): void  { this.questions.update(list => list.map(q => ({ ...q, selected: false }))); }

  toggleQuestionMenu(id: string, event: Event): void {
    event.stopPropagation();
    this.openMenuId.update(current => current === id ? null : id);
  }

  closeAllMenus(): void { this.openMenuId.set(null); }

  toggleCustomQuestion(): void {
    this.showCustomQuestion.update(v => !v);
    this.customQuestionText.set('');
    this.customQuestionTouched.set(false);
  }

  async saveCustomQuestion(): Promise<void> {
    this.customQuestionTouched.set(true);
    const text = this.customQuestionText().trim();
    if (!text || this.isSavingCustomQuestion()) return;
    const categoryId = this.selectedCategory();
    if (!categoryId) return;

    this.isSavingCustomQuestion.set(true);
    try {
      const res = await this.api.request<string>(
        'POST', API_ROUTES.ADD_CUSTOM_QUESTION,
        { categoryId, questionText: text },
        { showLoader: false, showToaster: false },
      );
      if (res.status && res.data) {
        this.questions.update(list => [
          ...list,
          { id: res.data!, text, source: 'Custom', sourceType: 2, selected: true, answered: false, isCustom: true },
        ]);
      }
      this.customQuestionText.set('');
      this.showCustomQuestion.set(false);
    } finally {
      this.isSavingCustomQuestion.set(false);
    }
  }

  startEditQuestion(id: string): void {
    const q = this.questions().find(q => q.id === id);
    if (!q?.isCustom) return;
    this.editingQuestionId.set(id);
    this.editingQuestionText.set(q.text);
    this.openMenuId.set(null);
  }

  cancelEditQuestion(): void {
    this.editingQuestionId.set(null);
    this.editingQuestionText.set('');
  }

  async saveEditQuestion(): Promise<void> {
    const id = this.editingQuestionId();
    const text = this.editingQuestionText().trim();
    if (!id || !text || this.isSavingEditQuestion()) return;

    this.isSavingEditQuestion.set(true);
    try {
      const res = await this.api.request<unknown>(
        'POST', API_ROUTES.UPDATE_CUSTOM_QUESTION,
        { customQuestionId: id, questionText: text },
        { showLoader: false, showToaster: false },
      );
      if (res.status) {
        this.questions.update(list => list.map(q => q.id === id ? { ...q, text } : q));
        this.editingQuestionId.set(null);
        this.editingQuestionText.set('');
      } else {
        // Keep editing mode open so the user doesn't lose their changes
        this.toast.error('Failed to save question. Please try again.');
      }
    } catch {
      this.toast.error('Failed to save question. Please try again.');
    } finally {
      this.isSavingEditQuestion.set(false);
    }
  }

  private async deleteCustomQuestionApi(id: string): Promise<void> {
    const res = await this.api.request<unknown>(
      'POST', API_ROUTES.DELETE_CUSTOM_QUESTION,
      { requestId: id },
      { showLoader: false, showToaster: false },
    );
    if (res.status) {
      this.questions.update(list => list.filter(q => q.id !== id));
    }
  }

  // -- Step 4: Recording -----------------------------------------------------

  readonly currentQuestionIndex   = signal(0);
  readonly recordingState         = signal<'idle' | 'recording' | 'stopped'>('idle');
  readonly recordingTimer         = signal(0);
  readonly recordedUrl            = signal<string | null>(null);
  readonly uploadedUrl            = signal<string | null>(null);
  readonly questionsAccordionOpen = signal(true);
  readonly showReloadWarning       = signal(false);
  readonly uploadErrorMsg           = signal<string | null>(null);
  readonly audioUploadErrorMsg      = signal<string | null>(null);
  readonly imageUploadErrorMsg      = signal<string | null>(null);
  readonly permissionError          = signal<string | null>(null);
  readonly isPermissionDenied       = signal(false);
  readonly questionTimeframes     = signal<Record<string, QuestionTimeframe>>({});
  readonly audioDragOver            = signal(false);
  readonly imageDragOver            = signal(false);

  /** Preview URL for the image uploaded in image+audio mode */
  readonly imagePreviewUrl = signal<string | null>(null);
  private imageFile: File | null = null;

  readonly videoRef = viewChild<ElementRef<HTMLVideoElement>>('videoPreview');
  readonly audioRef = viewChild<ElementRef<HTMLAudioElement>>('audioPreview');

  readonly currentQuestion = computed<Question | undefined>(
    () => this.selectedQuestions()[this.currentQuestionIndex()],
  );

  readonly isLastQuestion = computed(
    () => this.currentQuestionIndex() >= this.selectedQuestions().length - 1,
  );

  readonly allQuestionsAnswered = computed(
    () => this.selectedQuestions().length > 0 && this.selectedQuestions().every(q => q.answered),
  );

  readonly progressPercent = computed(() => {
    const total     = this.selectedQuestions().length;
    if (total === 0) return 0;
    const answered  = this.selectedQuestions().filter(q => q.answered).length;
    const effective = Math.min(answered + 1, total);
    return Math.round((effective / total) * 100);
  });

  private liveStream:    MediaStream | null     = null;
  private mediaRecorder: MediaRecorder | null   = null;
  private recordedChunks: Blob[]                = [];
  private timerInterval: ReturnType<typeof setInterval> | null = null;
  private currentQuestionStartTime              = 0;
  private pendingFinishReviewNavigation         = false;
  private keepMediaUrlsForReview                = false;
  private readonly onBeforeUnload = (e: BeforeUnloadEvent): void => { e.preventDefault(); };
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    const isRefresh = e.key === 'F5' || ((e.ctrlKey || e.metaKey) && e.key === 'r');
    if (isRefresh) { e.preventDefault(); this.showReloadWarning.set(true); }
  };

  toggleQuestionsAccordion(): void { this.questionsAccordionOpen.update(v => !v); }

  readonly canNavigateQuestions = computed<boolean>(() => {
    const media    = this.selectedMedia();
    const hasMedia = this.recordingState() !== 'idle' || !!this.uploadedUrl() || !!this.recordedUrl();
    if (media === 'video')       return hasMedia;
    if (media === 'audio')       return hasMedia;
    if (media === 'image-audio') return hasMedia;
    return true;
  });

  jumpToQuestion(index: number): void {
    if (!this.canNavigateQuestions()) return;
    const target = this.selectedQuestions()[index];
    if (target?.answered) return;
    if (index === this.currentQuestionIndex()) return; // already on this question
    this.saveCurrentTimeframe();
    const currentQ = this.currentQuestion();
    const targetId = this.selectedQuestions()[index]?.id;
    this.questions.update(list => list.map(q => {
      if (currentQ && q.id === currentQ.id) return { ...q, answered: true };
      if (targetId && q.id === targetId) return { ...q, answered: true };
      return q;
    }));
    this.currentQuestionIndex.set(index);
    this.currentQuestionStartTime = this.recordingTimer();
    // Save a placeholder timeframe for the newly active question so its badge
    // appears immediately. finishAndReview() will overwrite with the real endSecond.
    this.saveCurrentTimeframe();
  }

  toggleAnswered(id: string, event: Event): void {
    event.stopPropagation();
    if (!this.canNavigateQuestions()) return;
    this.questions.update(list => list.map(q => q.id === id ? { ...q, answered: !q.answered } : q));
  }

  nextQuestion(): void {
    if (!this.canNavigateQuestions()) return;
    this.saveCurrentTimeframe();
    const currentQ = this.currentQuestion();
    if (currentQ) {
      this.questions.update(list => list.map(q => q.id === currentQ.id ? { ...q, answered: true } : q));
    }
    if (!this.isLastQuestion()) {
      this.currentQuestionIndex.update(i => i + 1);
      this.currentQuestionStartTime = this.recordingTimer();
    }
  }

  async startVideoRecording(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    this.permissionError.set(null);
    this.isPermissionDenied.set(false);
    try {
      this.stopLiveStream();
      const prev = this.recordedUrl();
      // If media already exists, this is a re-record — reset answered state but keep questions visible
      if (prev || this.uploadedUrl()) {
        this.questions.update(list => list.map(q => ({ ...q, answered: false })));
        this.currentQuestionIndex.set(0);
      }
      if (prev) URL.revokeObjectURL(prev);
      this.recordedUrl.set(null);
      this.uploadedUrl.set(null);
      this.recordingTimer.set(0);
      this.questionTimeframes.set({});
      this.currentQuestionStartTime = 0;

      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      this.liveStream = stream;

      const video = this.videoRef()?.nativeElement;
      if (video) {
        video.srcObject = stream;
        video.removeAttribute('src');
        video.controls = false;
        video.muted    = true;
        await video.play().catch(() => {});
      }

      this.recordedChunks = [];
      const mimeType = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
        ? 'video/webm;codecs=vp8,opus'
        : 'video/webm';
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) this.recordedChunks.push(e.data); };
      mr.onstop = () => this.onRecordingStop();
      mr.start(100);
      this.mediaRecorder = mr;

      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => this.recordingTimer.update(t => t + 1), 1000);
      this.zone.run(() => { this.recordingState.set('recording'); this.cdr.markForCheck(); });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        this.isPermissionDenied.set(true);
        this.permissionError.set(
          'Camera or microphone access was blocked. Please allow access in your browser settings and reload the page.',
        );
      } else if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
        this.permissionError.set('No camera or microphone found. Please connect a device and try again.');
      } else {
        this.permissionError.set('Could not access camera or microphone. Please check your device.');
      }
      this.zone.run(() => this.cdr.markForCheck());
    }
  }

  retakeRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
    this.stopLiveStream();
    const prev = this.recordedUrl();
    if (prev) URL.revokeObjectURL(prev);
    this.recordedUrl.set(null);
    this.recordedChunks = [];
    this.recordingTimer.set(0);
    this.recordingState.set('idle');
    setTimeout(() => this.startVideoRecording(), 100);
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
  }

  async startAudioRecording(): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;
    this.permissionError.set(null);
    this.isPermissionDenied.set(false);
    try {
      this.stopLiveStream();
      const prev = this.recordedUrl();
      // If media already exists, this is a re-record — reset answered state but keep questions visible
      if (prev || this.uploadedUrl()) {
        this.questions.update(list => list.map(q => ({ ...q, answered: false })));
        this.currentQuestionIndex.set(0);
      }
      if (prev) URL.revokeObjectURL(prev);
      this.recordedUrl.set(null);
      this.uploadedUrl.set(null);
      this.recordingTimer.set(0);
      this.questionTimeframes.set({});
      this.currentQuestionStartTime = 0;

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.liveStream = stream;

      this.recordedChunks = [];
      // Prefer audio/mp4 (AAC → native .m4a on Safari/iOS);
      // fall back to opus-in-webm on Chrome/Firefox (saved as .m4a by the upload service).
      const mimeType =
        typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/mp4')
          ? 'audio/mp4'
          : typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
            ? 'audio/webm;codecs=opus'
            : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType });
      mr.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) this.recordedChunks.push(e.data); };
      mr.onstop = () => this.onRecordingStop();
      mr.start(100);
      this.mediaRecorder = mr;

      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => this.recordingTimer.update(t => t + 1), 1000);
      this.zone.run(() => { this.recordingState.set('recording'); this.cdr.markForCheck(); });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        this.isPermissionDenied.set(true);
        this.permissionError.set(
          'Microphone access was blocked. Please allow access in your browser settings and reload the page.',
        );
      } else if (err?.name === 'NotFoundError' || err?.name === 'DevicesNotFoundError') {
        this.permissionError.set('No microphone found. Please connect a device and try again.');
      } else {
        this.permissionError.set('Could not access microphone. Please check your device.');
      }
      this.zone.run(() => this.cdr.markForCheck());
    }
  }

  retakeAudioRecording(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') this.mediaRecorder.stop();
    if (this.timerInterval) { clearInterval(this.timerInterval); this.timerInterval = null; }
    this.stopLiveStream();
    const prev = this.recordedUrl();
    if (prev) URL.revokeObjectURL(prev);
    this.recordedUrl.set(null);
    this.recordedChunks = [];
    this.recordingTimer.set(0);
    this.recordingState.set('idle');
    setTimeout(() => this.startAudioRecording(), 100);
  }

  onAudioUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      this.audioUploadErrorMsg.set('Only audio files are allowed (MP3, WAV, AAC, M4A, etc.).');
      input.value = '';
      return;
    }
    const MAX_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      this.audioUploadErrorMsg.set(`File too large. Maximum allowed size is 500 MB.`);
      input.value = '';
      return;
    }
    this.audioUploadErrorMsg.set(null);
    this.questions.update(list => list.map(q => ({ ...q, answered: true })));
    const prev = this.uploadedUrl();
    if (prev) URL.revokeObjectURL(prev);
    const url = URL.createObjectURL(file);
    this.uploadedUrl.set(url);
    this.recordedUrl.set(null);
    this.recordingState.set('stopped');
    const audio = this.audioRef()?.nativeElement;
    if (audio) { audio.src = url; audio.load(); }
  }

  onImageForAudioUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      this.imageUploadErrorMsg.set('Only image files are allowed (JPEG, PNG, WebP, etc.).');
      input.value = '';
      this.cdr.markForCheck();
      return;
    }
    this.imageUploadErrorMsg.set(null);
    this.imageFile = file;
    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreviewUrl.set(e.target?.result as string);
      this.cdr.markForCheck();
    };
    reader.readAsDataURL(file);
  }

  removeImageForAudio(): void {
    this.imagePreviewUrl.set(null);
    this.imageFile = null;
  }

  // -- Drag-and-drop: audio mode -------------------------------------------

  onAudioDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.audioDragOver.set(true);
  }

  onAudioDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.audioDragOver.set(false);
  }

  onAudioDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.audioDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('audio/')) {
      this.audioUploadErrorMsg.set('Only audio files are allowed (MP3, WAV, AAC, M4A, etc.).');
      this.cdr.markForCheck();
      return;
    }
    const MAX_SIZE = 500 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      this.audioUploadErrorMsg.set('File too large. Maximum allowed size is 500 MB.');
      this.cdr.markForCheck();
      return;
    }
    this.audioUploadErrorMsg.set(null);
    this.questions.update(list => list.map(q => ({ ...q, answered: true })));
    const prev = this.uploadedUrl();
    if (prev) URL.revokeObjectURL(prev);
    const url = URL.createObjectURL(file);
    this.uploadedUrl.set(url);
    this.recordedUrl.set(null);
    this.recordingState.set('stopped');
    const audio = this.audioRef()?.nativeElement;
    if (audio) { audio.src = url; audio.load(); }
    this.cdr.markForCheck();
  }

  // -- Drag-and-drop: image+audio mode (accepts image OR audio by file type) -

  onImageAreaDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.imageDragOver.set(true);
  }

  onImageAreaDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.imageDragOver.set(false);
  }

  onImageAreaDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.imageDragOver.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (!file) return;
    if (file.type.startsWith('image/')) {
      this.imageFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.imagePreviewUrl.set(e.target?.result as string);
        this.cdr.markForCheck();
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('audio/')) {
      const MAX_SIZE = 500 * 1024 * 1024;
      if (file.size > MAX_SIZE) {
        this.audioUploadErrorMsg.set('File too large. Maximum allowed size is 500 MB.');
        this.cdr.markForCheck();
        return;
      }
      this.audioUploadErrorMsg.set(null);
      this.questions.update(list => list.map(q => ({ ...q, answered: true })));
      const prev = this.uploadedUrl();
      if (prev) URL.revokeObjectURL(prev);
      const url = URL.createObjectURL(file);
      this.uploadedUrl.set(url);
      this.recordedUrl.set(null);
      this.recordingState.set('stopped');
      const audio = this.audioRef()?.nativeElement;
      if (audio) { audio.src = url; audio.load(); }
      this.cdr.markForCheck();
    }
  }

  onVideoUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file  = input.files?.[0];
    if (!file) return;
    // Validate file type
    if (!file.type.startsWith('video/')) {
      this.uploadErrorMsg.set('Only video files are allowed (MP4, MOV, WebM, etc.).');
      input.value = '';
      return;
    }
    // Validate file size (max 1 GB)
    const MAX_SIZE = 1 * 1024 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      const sizeMb = (file.size / (1024 * 1024)).toFixed(0);
      this.uploadErrorMsg.set(`File is too large (${sizeMb} MB). Maximum allowed size is 1 GB.`);
      input.value = '';
      return;
    }
    this.uploadErrorMsg.set(null);
    // Mark all questions as answered when a video is successfully uploaded
    this.questions.update(list => list.map(q => ({ ...q, answered: true })));
    const prev = this.uploadedUrl();
    if (prev) URL.revokeObjectURL(prev);
    const url = URL.createObjectURL(file);
    this.uploadedUrl.set(url);
    this.recordedUrl.set(null);
    this.recordingState.set('stopped');
    const video = this.videoRef()?.nativeElement;
    if (video) { video.srcObject = null; video.src = url; video.controls = true; video.muted = false; video.load(); }
  }

  finishAndReview(): void {
    this.saveCurrentTimeframe();
    const currentQ = this.currentQuestion();
    if (currentQ) {
      this.questions.update(list => list.map(q => q.id === currentQ.id ? { ...q, answered: true } : q));
    }
    if (this.recordingState() === 'recording') {
      this.pendingFinishReviewNavigation = true;
      this.stopRecording();
      return;
    }
    this.navigateToReview();
  }

  getQuestionTimeframe(questionId: string): QuestionTimeframe | undefined {
    return this.questionTimeframes()[questionId];
  }

  getAllTimeframes(): QuestionTimeframe[] {
    return Object.values(this.questionTimeframes());
  }

  formatRecordingTime(seconds: number): string {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  }

  // -- Lifecycle -------------------------------------------------------------

  ngOnInit(): void {
    this.layoutService.setHideHeader(true);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    window.addEventListener('keydown', this.onKeyDown);
    this.loadCategories();
    const navState = (this.router.getCurrentNavigation()?.extras.state ?? history.state ?? {}) as Record<string, unknown>;
    if (navState['returnFromReview'] && isPlatformBrowser(this.platformId)) {
      this.restoreFromReview();
    }
  }

  dismissReloadWarning(): void { this.showReloadWarning.set(false); }

  confirmReload(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    location.reload();
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    window.removeEventListener('keydown', this.onKeyDown);
    this.layoutService.setHideHeader(false);
    this.stopLiveStream();
    if (this.timerInterval) clearInterval(this.timerInterval);
    if (!this.keepMediaUrlsForReview) {
      const ru = this.recordedUrl();
      const uu = this.uploadedUrl();
      if (ru) URL.revokeObjectURL(ru);
      if (uu) URL.revokeObjectURL(uu);
    }
  }

  // -- Private helpers -------------------------------------------------------

  private restoreFromReview(): void {
    const videoUrl  = sessionStorage.getItem('fm_story_review_video_url');
    const imageUrl  = sessionStorage.getItem('fm_story_review_image_url');
    const mediaType = sessionStorage.getItem('fm_story_record_media_type');
    const catId     = sessionStorage.getItem('fm_story_record_category_id');

    if (mediaType) this.selectedMedia.set(mediaType);
    if (catId)     this.selectedCategory.set(Number(catId));
    if (imageUrl)  this.imagePreviewUrl.set(imageUrl);

    if (videoUrl) {
      this.recordedUrl.set(videoUrl);
      this.uploadedUrl.set(null);
      this.recordingState.set('stopped');
    }

    const raw = sessionStorage.getItem('fm_story_record_questions');
    if (raw) {
      try { this.questions.set(JSON.parse(raw) as Question[]); } catch (_) {}
    }

    const rawTf = sessionStorage.getItem('fm_story_record_timeframes');
    if (rawTf) {
      try { this.questionTimeframes.set(JSON.parse(rawTf)); } catch (_) {}
    }

    this.currentStep.set(4);
    this.currentQuestionIndex.set(0);

    // Wire the media element src after Angular renders the step-4 view
    setTimeout(() => {
      if (!videoUrl) return;
      const type = mediaType ?? 'video';
      if (type === 'video') {
        const video = this.videoRef()?.nativeElement;
        if (video) { video.srcObject = null; video.src = videoUrl; video.controls = true; video.muted = false; video.load(); }
      } else {
        const audio = this.audioRef()?.nativeElement;
        if (audio) { audio.src = videoUrl; audio.load(); }
      }
      this.cdr.markForCheck();
    }, 100);
  }

  private navigateToReview(): void {
    this.keepMediaUrlsForReview = true;
    const mediaUrl       = this.uploadedUrl() || this.recordedUrl();
    const imageUrl       = this.imagePreviewUrl();
    const selectedTexts  = this.selectedQuestions().map(q => q.text).filter(Boolean);
    const transcriptSeed = selectedTexts.length
      ? selectedTexts.map((q, idx) => `${idx + 1}. ${q}`).join('\n')
      : (this.currentQuestion()?.text ?? '');

    if (isPlatformBrowser(this.platformId)) {
      if (mediaUrl) sessionStorage.setItem('fm_story_review_video_url', mediaUrl);
      else          sessionStorage.removeItem('fm_story_review_video_url');
      if (imageUrl) sessionStorage.setItem('fm_story_review_image_url', imageUrl);
      else          sessionStorage.removeItem('fm_story_review_image_url');
      sessionStorage.setItem('fm_story_record_questions', JSON.stringify(this.questions()));
      sessionStorage.setItem('fm_story_record_timeframes', JSON.stringify(this.questionTimeframes()));
      sessionStorage.setItem('fm_story_review_transcript_verbatim',  transcriptSeed);
      sessionStorage.setItem('fm_story_review_transcript_narrative', transcriptSeed);
      sessionStorage.setItem('fm_story_review_navigated', '1');
      sessionStorage.setItem('fm_story_record_media_type', this.selectedMedia());
      if (this.selectedCategory() != null) sessionStorage.setItem('fm_story_record_category_id', String(this.selectedCategory()));
    }

    this.router.navigate(['/app/stories/record/review'], {
      state: {
        videoUrl:            mediaUrl,
        imageUrl,
        mediaType:           this.selectedMedia(),
        storyTitle:          '',
        transcriptVerbatim:  transcriptSeed,
        transcriptNarrative: transcriptSeed,
        categoryId:          this.selectedCategory(),
        questions:           this.selectedQuestions(),
        questionTimeframes:  this.questionTimeframes(),
        videoDuration:       this.recordingTimer(),
      },
    });
  }

  private onRecordingStop(): void {
    const isAudio = this.selectedMedia() === 'audio' || this.selectedMedia() === 'image-audio';
    // Use the actual mimeType the MediaRecorder used (audio/mp4 or audio/webm);
    // fall back to the generic type when the recorder is already gone.
    const blobType = this.mediaRecorder?.mimeType || (isAudio ? 'audio/webm' : 'video/webm');
    const blob = new Blob(this.recordedChunks, { type: blobType });
    const url  = URL.createObjectURL(blob);
    this.recordedUrl.set(url);
    if (!isAudio) {
      const video = this.videoRef()?.nativeElement;
      if (video) { video.srcObject = null; video.src = url; video.controls = true; video.muted = false; video.load(); }
    } else {
      const audio = this.audioRef()?.nativeElement;
      if (audio) { audio.src = url; audio.load(); }
    }
    this.stopLiveStream();
    this.recordingState.set('stopped');
    if (this.pendingFinishReviewNavigation) {
      this.pendingFinishReviewNavigation = false;
      this.navigateToReview();
    }
  }

  private stopLiveStream(): void {
    this.liveStream?.getTracks().forEach(t => t.stop());
    this.liveStream = null;
  }

  private saveCurrentTimeframe(): void {
    const q = this.currentQuestion();
    if (!q || this.recordingState() !== 'recording') return;
    const endSecond = this.recordingTimer();
    this.questionTimeframes.update(map => ({
      ...map,
      [q.id]: { questionId: q.id, startSecond: this.currentQuestionStartTime, endSecond },
    }));
  }
}
